import { gridOf, type Board } from '@archon3d/core';
import { StandardMaterial } from '@babylonjs/core/Materials/standardMaterial';
import { Color3 } from '@babylonjs/core/Maths/math.color';
import { Vector3 } from '@babylonjs/core/Maths/math.vector';
import type { Mesh } from '@babylonjs/core/Meshes/mesh';
import { MeshBuilder } from '@babylonjs/core/Meshes/meshBuilder';
import type { TransformNode } from '@babylonjs/core/Meshes/transformNode';
import type { Scene } from '@babylonjs/core/scene';
import { BOARD_CENTER, COLORS, FRAME_THICKNESS, TABLE_HEIGHT, TILE_SIZE } from './constants';
import { boardFootprint, hexInradius } from './layout';

export interface BoardFrame {
  /** `metadata.sides` says whether it is the box (4) or the six-sided prism (6). */
  mesh: Mesh;
  dispose(): void;
}

/** The frame reaches this far beyond the tiles on each side, in total. */
const FRAME_MARGIN = 0.05;

/**
 * How far the tiles reach from the board's centre in the direction that decides whether the
 * board fits on the table: a square board's half-diagonal, since its corners may point anywhere
 * as it turns to a seat; a hex board's inradius, since its sides stay parallel with the table's.
 */
export function boardReach(board: Board): number {
  if (gridOf(board) === 'hex') return hexInradius(board) * TILE_SIZE;
  const { width, depth } = boardFootprint(board);
  return Math.hypot(width, depth) / 2;
}

/**
 * The dark frame under the tiles: a box round a square board, a six-sided prism round a hex
 * board, snug to the hexagon the cells fill (a hexagon of hexes fits a flat-top hexagon exactly;
 * anything else gets the smallest one that holds it). The prism is Babylon's, corners on ±X like
 * the table's, which is what the tiles' hexagon has too: its corners point east and west.
 */
export function createBoardFrame(
  scene: Scene,
  board: Board,
  parent: TransformNode | null,
): BoardFrame {
  let mesh: Mesh;
  if (gridOf(board) === 'hex') {
    const inradius = hexInradius(board) * TILE_SIZE + FRAME_MARGIN / 2;
    mesh = MeshBuilder.CreateCylinder(
      'boardFrame',
      {
        diameter: (2 * inradius) / Math.cos(Math.PI / 6),
        height: FRAME_THICKNESS,
        tessellation: 6,
      },
      scene,
    );
    mesh.metadata = { sides: 6 };
  } else {
    const footprint = boardFootprint(board);
    mesh = MeshBuilder.CreateBox(
      'boardFrame',
      {
        width: footprint.width + FRAME_MARGIN,
        height: FRAME_THICKNESS,
        depth: footprint.depth + FRAME_MARGIN,
      },
      scene,
    );
    mesh.metadata = { sides: 4 };
  }
  mesh.position = new Vector3(BOARD_CENTER.x, TABLE_HEIGHT + FRAME_THICKNESS / 2, BOARD_CENTER.z);
  const material = new StandardMaterial('frameMaterial', scene);
  material.diffuseColor = Color3.FromHexString(COLORS.frame);
  material.specularColor = new Color3(0.2, 0.2, 0.22);
  mesh.material = material;
  mesh.parent = parent;
  mesh.receiveShadows = true;
  mesh.isPickable = false;
  return {
    mesh,
    dispose() {
      mesh.dispose();
      material.dispose();
    },
  };
}
